<template>
  <div class="product-list" :class="{ 'grid-mode': isGrid }">
    <!-- 布局切换器 -->
    <div v-if="showLayoutSwitcher" class="layout-switcher">
      <van-space>
        <van-button
          :type="layout === 'grid' ? 'primary' : 'default'"
          size="small"
          @click="switchLayout('grid')"
        >
          双列
        </van-button>
        <van-button
          :type="layout === 'list' ? 'primary' : 'default'"
          size="small"
          @click="switchLayout('list')"
        >
          单列
        </van-button>
      </van-space>
    </div>

    <!-- 商品列表内容区域 -->
    <div class="content-wrapper">
      <!-- 分类 Banner -->
      <div class="category-banner" v-if="banner">
        <img :src="banner.imageUrl" :alt="banner.title" />
      </div>

      <!-- 商品列表 -->
      <van-pull-refresh
        v-model="refreshing"
        @refresh="onRefresh"
        :disabled="!isAtTop"
        :pulling-text="''"
        :loosing-text="''"
        :loading-text="''"
        :success-text="''"
        :head-height="50"
        :pull-distance="60"
      >
        <van-list
          v-model:loading="loading"
          :finished="finished"
          @load="onLoad"
          class="list-container"
        >
          <!-- 产品列表 -->
          <div class="products-wrapper" :class="layout">
            <div
              v-for="product in products"
              :key="product.id"
              class="product-card"
              @click="handleProductClick(product.url)"
            >
              <div class="image-wrapper">
                <img
                  :src="product.imageUrl || '/images/default.png'"
                  :alt="product.name"
                  @error="handleImageError"
                  loading="lazy"
                  class="product-image"
                />
                <img
                  v-if="cornerImage"
                  :src="cornerImage"
                  class="corner-image"
                  alt="corner image"
                />
              </div>
              <div class="product-info">
                <div class="product-name">{{ product.name }}</div>
                <div
                  class="product-desc"
                  v-if="layout === 'list' && product.description"
                >
                  {{ product.description }}
                </div>
                <div class="product-price">
                  <template v-if="layout === 'grid'">
                    <!-- 双列模式下的产品价格 -->
                    <div class="price-wrapper">
                      <div class="current-price">
                        <!-- 控制参数：priceDescription（是否显示价格说明） -->
                        <span
                          v-if="priceDescription.length > 0"
                          class="price-description"
                        >
                          {{ priceDescription }}
                        </span>
                        <span class="price">
                          <small class="currency">¥</small>{{ product.price }}
                        </span>
                      </div>
                      <!-- 双列模式下的产品参考价 
                           控制参数：lineprice（是否显示）、linepriceitem（参考价字段） -->
                      <div
                        class="original-price"
                        v-if="lineprice && product[linepriceitem]"
                      >
                        原价：¥{{ product[linepriceitem] }}
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <span class="price">￥{{ product.price }}</span>
                    <span class="original-price" v-if="product.originalPrice"
                      ><small class="currency">¥</small
                      >{{ product.originalPrice }}</span
                    >
                  </template>
                </div>
                <!-- <div class="add-to-cart" @click.stop="handleAddToCart(product)">
                  <van-icon name="plus" />
                </div> -->
              </div>
            </div>
          </div>

          <!-- 列表底部内容 -->
          <div class="list-footer" v-if="finished">
            <template v-if="imagedisplay && imagedisplayImgurl">
              <ImageDisplay
                :imageUrl="imagedisplayImgurl"
                :url="imagedisplayUrl"
              />
            </template>
            <template v-else>
              <div class="finished-text">没有更多了</div>
            </template>
          </div>
        </van-list>
      </van-pull-refresh>
    </div>

    <!-- 加载中状态 -->
    <div class="loading-overlay" v-if="initialLoading">
      <van-loading type="spinner" color="#ff6b6b" />
    </div>

    <!-- 错误提示 -->
    <div class="error-tip" v-if="error">
      <van-empty :description="error" image="error">
        <template #bottom>
          <van-button round type="primary" @click="retryLoad">重试</van-button>
        </template>
      </van-empty>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted, nextTick, onActivated } from "vue";
import { showToast } from "vant";
import ImageDisplay from "./ImageDisplay.vue";
import {
  getCategoryProducts,
  getSpecialPriceProducts,
} from "@/api/advertisement";
import { getFeatureZoneList } from "@/api/featurezone";
import { getProductSearchResult } from "@/api/product";

export default {
  name: "ProductList",
  components: {
    ImageDisplay,
  },
  props: {
    isGrid: {
      type: Boolean,
      default: false
    },
    categoryId: {
      type: [String, Number, null],
      default: null,
    },
    subCategoryId: {
      type: [String, Number, null],
      default: null,
    },
    isFromCategory: {
      type: Boolean,
      default: false,
    },
    referenceCode: {
      type: String,
      default: null,
    },
    advertCode: {
      type: String,
      default: null,
    },
    layout: {
      // 新增布局参数
      type: String,
      default: "grid", // 默认值为网格布局
    },
    keyword: {
      // 关键字
      type: String,
      default: "", // 默认值为空值
    },
    showLayoutSwitcher: {
      type: Boolean, // 布局切换器显示控制
      default: true,
    },
    lineprice: {
      type: Boolean,
      default: false,
    },
    linepriceitem: {
      type: String,
      default: "originalPrice",
    },
    backgroundColor: {
      type: String,
      default: "#fff",
    },
    cornerImage: {
      type: String,
      default: "",
      required: false,
    },
    imagedisplay: {
      type: Boolean,
      default: false,
    },
    imagedisplayImgurl: {
      type: String,
      default: "",
    },
    imagedisplayUrl: {
      type: String,
      default: "",
    },
    priceDescription: {
      type: String,
      default: "",
    },
  },
  emits: ["mounted"],

  setup(props, { emit }) {
    // 添加节流函数定义
    const throttle = (fn, delay) => {
      let timer = null;
      let lastRun = 0;
      return function (...args) {
        const now = Date.now();
        if (lastRun && now < lastRun + delay) {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            lastRun = now;
            fn.apply(this, args);
          }, delay);
        } else {
          lastRun = now;
          fn.apply(this, args);
        }
      };
    };

    const products = ref([]);
    const loading = ref(false);
    const finished = ref(false);
    const refreshing = ref(false);
    const initialLoading = ref(true);
    const error = ref(null);
    const currentPage = ref(1);
    const pageSize = 10;
    const kind = "";
    const layout = ref(props.layout); // 使用传入的布局参数

    // 添加一个标志位，用于防止重复触发
    const isLoading = ref(false);

    // 在 setup 中添加一个 ref 来存父容器
    const productsContainer = ref(null);
    const parseImageUrl = (imageUrlString) => {
      try {
        const imageData = JSON.parse(imageUrlString)[0];
        return imageData.ServerUrl + imageData.FilePath;
      } catch (error) {
        console.error("解析图片URL失败:", error);
        return "";
      }
    };

    // 在 setup 中添加默认图片常量
    const DEFAULT_IMAGE =
      "https://dummyimage.com/400x400/eeeeee/999999&text=No+Image";

    // 在 setup 中添加
    const DEBUG = process.env.NODE_ENV === "development";

    // 在 setup 中添加调试日志函数
    const log = (...args) => {
      if (DEBUG) {
        console.log(...args);
      }
    };

    // 添加 isAtTop ref
    const isAtTop = ref(true);
    const banner = ref({
      imageUrl: '',
      title: ''
    });

    // 添加一个 ref 来存储上一次的属性值
    const prevProps = ref({
      categoryId: props.categoryId,
      subCategoryId: props.subCategoryId,
      referenceCode: props.referenceCode,
      advertCode: props.advertCode,
      keyword: props.keyword,
    });

    // 修改滚动到顶部的方法
    const scrollToTop = () => {
      // 同时处理文档和容器的滚动
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      const listContainer = document.querySelector('.content-wrapper');
      if (listContainer) {
        listContainer.scrollTop = 0;
      }
    };

    // Watch 效果
    watch(
      [
        () => props.categoryId,
        () => props.subCategoryId,
        () => props.referenceCode,
        () => props.advertCode,
        () => props.keyword,
      ],
      async ([newCategoryId, newSubCategoryId, newReferenceCode, newAdvertCode, newKeyword],
             [oldCategoryId, oldSubCategoryId, oldReferenceCode, oldAdvertCode, oldKeyword]) => {
        // 确保组件已经挂载
        if (!initialLoading.value) {
          return;
        }

        if (
          props.categoryId ||
          props.subCategoryId ||
          props.referenceCode ||
          props.advertCode ||
          props.keyword
        ) {
          currentPage.value = 1;
          finished.value = false;
          error.value = null;
          isLoading.value = false;

          const shouldScroll =
            newCategoryId !== oldCategoryId ||
            newSubCategoryId !== oldSubCategoryId ||
            newReferenceCode !== oldReferenceCode ||
            newAdvertCode !== oldAdvertCode ||
            newKeyword !== oldKeyword;

          try {
            await loadProducts(true);
            if (shouldScroll) {
              nextTick(() => {
                scrollToTop();
              });
            }
          } catch (err) {
            console.error('Failed to load products:', err);
            error.value = err.message;
          }
        }
      },
      { immediate: false } // 设置为 false，防止立即执行
    );

    // 修改 onActivated 钩子，只在 keep-alive 状态下处理滚动
    onActivated(() => {
      const hasChanged = 
        prevProps.value.categoryId !== props.categoryId ||
        prevProps.value.subCategoryId !== props.subCategoryId ||
        prevProps.value.referenceCode !== props.referenceCode ||
        prevProps.value.advertCode !== props.advertCode ||
        prevProps.value.keyword !== props.keyword;

      if (hasChanged) {
        // 更新 prevProps 的值
        prevProps.value = {
          categoryId: props.categoryId,
          subCategoryId: props.subCategoryId,
          referenceCode: props.referenceCode,
          advertCode: props.advertCode,
          keyword: props.keyword
        };
      }
    });

    // 加载商品数据
    const loadProducts = async (isRefresh = false) => {
      if (isRefresh) {
        currentPage.value = 1;
        products.value = [];
        finished.value = false;
        isLoading.value = false;
      }

      try {
        let response;

        // 根据不同参数调用不同的 API
        if (props.advertCode) {
          try {
            response = await getFeatureZoneList(
              props.advertCode,
              currentPage.value || 1,
              pageSize || 10,
              kind || "PRODUCT"
            );
            // console.log("(advertCode) API response", response)

            // if (isRefresh) {
            //   products.value = newProducts;
            // } else {
            //   products.value.push(...newProducts);
            // }

            // finished.value = newProducts.length < pageSize;
            // error.value = null;
            // return;
          } catch (err) {
            console.error("advertCode products loading error:", err);
            error.value = "加载商品失败，请稍后重试";
            throw err;
          }
        } else if (props.referenceCode) {
          // 使用 referenceCode 获取特价商品
          response = await getSpecialPriceProducts({
            referenceCode: props.referenceCode,
            pageIndex: currentPage.value,
            pageSize: pageSize,
          });
        } else if (props.subCategoryId || props.categoryId) {
          // 使用 typeId 获取常规商品列表
          const params = {
            TypeId: props.subCategoryId || props.categoryId,
            PageIndex: currentPage.value,
            PageSize: pageSize,
          };
          response = await getCategoryProducts(params);
        } else if (props.keyword) {
          // console.log("keyword search request:", {
          //   keyword: props.keyword,
          //   pageIndex: currentPage.value,
          //   pageSize: pageSize,
          // });
          // 使用关键字获搜索结果
          response = await getProductSearchResult({
            keyword: props.keyword || "",
            pageIndex: currentPage.value,
            pageSize: pageSize,
          });
          // console.log("keyword response", response.Result.List)
        }
        // console.log("API response", response.ErrorCode)

        if (response.ErrorCode === 0 || response.ErrorCode === "0000") {
          const newProducts =
            response.Data?.List ?? response.Data ?? response.Result?.List;

          const total =
            parseInt(response.Data?.Total ?? response.Result?.Total) ??
            newProducts.length;
          // console.log("newProducts: ", newProducts.length)
          // console.log("total: ", total)
          // console.log("finished: ", finished.value)

          // 处理新商品数据
          if (isRefresh) {
            products.value = newProducts;
          } else {
            products.value.push(...newProducts);
          }

          // 修改分页状态判断逻辑
          // console.log("total2: ", total === 0)

          if (newProducts.length < 1) {
            // 只有当获取到的新数据条数小于1时，才标记为束
            finished.value = true;
          } else {
            // console.log("newProducts.length2: ", newProducts.length)
            finished.value = false;
          }

          error.value = null;
        } else {
          throw new Error(response.ErrorMessage || "获取商品列表失败");
        }
      } catch (err) {
        console.error("加载失败:", err);
        error.value = err.message;
        emit("error", err);
      } finally {
        loading.value = false;
        refreshing.value = false;
        initialLoading.value = false;
      }
    };

    // 监听所有可能触发数据加载的属性变化
    watch(
      [
        () => props.categoryId,
        () => props.subCategoryId,
        () => props.referenceCode,
        () => props.advertCode,
        () => props.keyword,
      ],
      (
        [
          newCategoryId,
          newSubCategoryId,
          newReferenceCode,
          newAdvertCode,
          newKeyword,
        ],
        [
          oldCategoryId,
          oldSubCategoryId,
          oldReferenceCode,
          oldAdvertCode,
          oldKeyword,
        ]
      ) => {
        if (
          props.categoryId ||
          props.subCategoryId ||
          props.referenceCode ||
          props.advertCode ||
          props.keyword
        ) {
          currentPage.value = 1;
          finished.value = false;
          initialLoading.value = true;
          error.value = null;
          isLoading.value = false;

          const shouldScroll =
            newCategoryId !== oldCategoryId ||
            newSubCategoryId !== oldSubCategoryId ||
            newReferenceCode !== oldReferenceCode ||
            newAdvertCode !== oldAdvertCode ||
            newKeyword !== oldKeyword;

          if (shouldScroll) {
            loadProducts(true).then(() => {
              nextTick(() => {
                scrollToTop();
              });
            });
          } else {
            loadProducts(true);
          }
        }
      },
      { immediate: true }
    );

    // 下拉刷新
    const onRefresh = () => {
      finished.value = false;
      loadProducts(true);
    };

    // 修改上拉加载函数
    // console.log("currentPage: ", currentPage.value)
    const onLoad = () => {
      // console.log("触发加载更多检查", {
      //   loading: loading.value,
      //   finished: finished.value,
      //   当前页: currentPage.value,
      //   isLoading: isLoading.value,
      //   产品数量: products.value.length,
      // });
      // console.log("onLoad triggered - current state:", {
      //   isLoading: isLoading.value,
      //   loading: loading.value,
      //   finished: finished.value,
      //   currentPage: currentPage.value,
      // });

      if (loading.value || finished.value || isLoading.value) {
        loading.value = false;
        // console.log("加载被阻止", {
        //   loading: loading.value,
        //   finished: finished.value,
        //   isLoading: isLoading.value,
        // });
        return;
      }

      // 在确认要发起请求时才增加页码

      currentPage.value += 1;
      console.log("开始加载第", currentPage.value, "页");

      isLoading.value = true;
      loading.value = true;
      loadProducts().finally(() => {
        isLoading.value = false;
      });
    };

    // 切换布局
    const switchLayout = (newLayout) => {
      layout.value = newLayout;
    };

    // 处理商品点击
    const handleProductClick = (url) => {
      if (url) {
        // 不论是外部链接还是内部路，都在当前窗口打开
        window.location.href = url;
      }
    };

    // 处理加入购物车
    const handleAddToCart = (product) => {
      showToast("加入购物车成功");
    };

    // 重试加载
    const retryLoad = () => {
      error.value = null;
      initialLoading.value = true;
      loadProducts(true);
    };

    // 修改 handleImageError 函数
    const handleImageError = (e) => {
      e.target.src = DEFAULT_IMAGE;
    };

    // 修改 isNearBottom 函数
    const isNearBottom = () => {
      // console.log("isNearBottom check - DOM elements:", {
      //   hasProductsWrapper: !!document.querySelector(".products-wrapper"),
      //   hasProductCards: !!document.querySelector(".product-card"),
      //   totalCards: document.querySelectorAll(".product-card").length,
      // });
      // 获取产品列表器
      const productsWrapper = document.querySelector(".products-wrapper");
      if (!productsWrapper) {
        console.log("isNearBottom: 未找到产品列表容器");
        return false;
      }

      // 获取最后一个产品卡片
      const lastCard = productsWrapper.querySelector(
        ".product-card:last-child"
      );
      if (!lastCard) {
        console.log("isNearBottom: 未找到产品卡片");
        return false;
      }

      // 获取视口和滚动信息
      const windowHeight = window.innerHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const lastCardRect = lastCard.getBoundingClientRect();
      const threshold = 200;

      // 计算是否接近底部
      const distanceToBottom = lastCardRect.bottom - windowHeight;
      const isNear = distanceToBottom <= threshold;

      console.log("滚动检测:", {
        windowHeight, // 视口高度
        scrollTop, // 滚动位置
        // lastCardBottom: lastCardRect.bottom, // 最后一个卡片底部位置
        // distanceToBottom, // 距离底部的距离
        // threshold, // 触发阈值
        // isNear, // 是否接近底部
        // productsCount: products.value.length, // ��前产品数量
        // currentPage: currentPage.value, // 当前页码
        // loading: loading.value, // 是否正在加载
        // finished: finished.value, // 是否加载完成
        // isLoading: isLoading.value, // 是否在加载中
      });

      return isNear;
    };

    // 修改滚动事件监听设置
    onMounted(async () => {
      emit("mounted");
      try {
        await loadProducts(true);
      } catch (err) {
        console.error('Initial load failed:', err);
        error.value = err.message;
      } finally {
        initialLoading.value = false;
      }

      const handleScroll = throttle(() => {
        // 检查是否在顶部（允许 10px 的误差）
        isAtTop.value = window.pageYOffset < 10;

        if (!loading.value && !finished.value && !isLoading.value) {
          if (isNearBottom()) {
            onLoad();
          }
        }
      }, 200);

      // 监听 window 的滚动事件
      // console.log("添加滚动监听器到 window");
      window.addEventListener("scroll", handleScroll);

      // 组件卸载时移除监听
      onUnmounted(() => {
        // console.log("移除滚动监听器");
        window.removeEventListener("scroll", handleScroll);
      });
    });

    return {
      layout,
      products,
      loading,
      finished,
      refreshing,
      initialLoading,
      error,
      switchLayout,
      onRefresh,
      onLoad,
      handleProductClick,
      handleAddToCart,
      retryLoad,
      handleImageError,
      isAtTop,
      scrollToTop,
    };
  },
};
</script>

<style lang="scss" scoped>
.product-list {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: v-bind(backgroundColor);
  margin: 0;
  padding: 0 2%;

  .layout-switcher {
    position: relative;
    // top: 0;
    // top: 40px; /* header(44px) + nav(40px) */
    z-index: 98;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 12px;
    background: #fff;
    border-bottom: 1px solid #f5f5f5;
  }

  .content-wrapper {
    padding: 8px;
    // margin-top: 40px;

    .products-wrapper {
      &.grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
        // flex-direction: flex;
      }

      &.list {
        .product-card {
          display: flex;
          margin-bottom: 12px;
          padding: 12px;

          .image-wrapper {
            width: 120px; // 固定图片容器宽度
            height: auto; // 固定图片容器高度
            flex-shrink: 0; // 防止图片区域被压缩
            margin-right: 12px; // 添加右侧间距

            &::before {
              padding-top: 0; // 移除之前的宽高比控制
            }

            img {
              position: relative; // 改变定位方式
              top: 0;
              left: 0;
              transform: none; // 移除居中变换
              width: 100%;
              height: 100%;
              object-fit: cover; // 保持图片比例
            }
          }

          .product-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 0; // 移除内边距

            .product-name {
              font-size: 16px;
              font-weight: 500;
              margin-bottom: 8px;
              -webkit-line-clamp: 2; // 限制两行
            }

            .product-desc {
              font-size: 14px; // 比产品名称小2号
              color: #999; // 灰色字体
              margin-bottom: 8px;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2; // 限制两行
              overflow: hidden;
            }

            .product-price {
              display: flex;
              flex-direction: column;
              align-items: flex-start;

              .price {
                color: #ff6b6b;
                font-size: 16px;
                font-weight: bold;
                display: flex;
                align-items: baseline;

                .currency {
                  font-size: 14px;
                  margin-right: 2px;
                }
              }

              .original-price {
                color: #999;
                font-size: 12px;
                text-decoration: line-through;
                margin-top: 4px;
                display: block;
              }
            }

            .add-to-cart {
              position: absolute;
              right: 0; // 调位置
              bottom: 0; // 与价格底部对齐
              transform: translateY(-2px); // 微调以实现视觉上的对齐
            }
          }
        }
      }
    }
  }

  .product-card {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08); // 添加阴影效果

    .image-wrapper {
      width: 100%;
      position: relative;
      overflow: hidden;
      background: #f5f5f5;
      border-radius: 6px;

      .product-image {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 6px;
      }

      .corner-image {
        position: absolute;
        top: 18px;
        left: 23px;
        width: 70px;
        height: auto;
        z-index: 2;
        pointer-events: none;
      }

      &::before {
        content: "";
        display: block;
        padding-top: 100%;
      }

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 6px;
      }
    }

    .product-info {
      padding: 8px;
      position: relative;
    }

    .product-name {
      font-size: 14px;
      color: #333;
      margin-bottom: 8px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .product-price {
      display: flex;
      align-items: baseline;

      .price {
        color: #ff6b6b;
        font-size: 16px;
        font-weight: bold;
      }

      .original-price {
        color: #999;
        font-size: 12px;
        text-decoration: line-through;
        margin-left: 4px;
      }
    }

    .add-to-cart {
      position: absolute;
      right: 8px;
      bottom: 8px;
      width: 24px;
      height: 24px;
      background: #ff6b6b;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      cursor: pointer;

      &:active {
        opacity: 0.8;
      }
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .error-tip {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
  }

  .grid-mode {
    .currency {
      font-size: 0.8em;
      margin-right: 2px;
    }

    .original-price {
      display: none;
    }

    .current-price {
      display: flex;
      align-items: baseline;
    }
  }
}

.price-wrapper {
  .current-price {
    display: flex;
    align-items: baseline;

    .price-description {
      color: #ff6b6b;
      font-weight: bold;
    }
  }

  .price {
    color: #ff6b6b;
    font-size: 16px;
    font-weight: bold;
  }

  .original-price {
    color: #999;
    font-size: 12px;
    text-decoration: line-through;
    margin-top: 4px;
    display: flex;
    align-items: center;
    /*margin-left: 4px;  调整左边距 */
  }
}

.products-container {
  flex: 1;
  margin-left: 90px;
  padding: 0;
  background: #fff;
  min-height: 100%;
  position: relative;
  overflow-y: auto;

  @media screen and (max-width: 375px) {
    margin-left: 80px;
  }
}

.layout-controller {
  &.fixed {
    position: sticky;
    top: 254px; /* header(44px) + banner(170px) + nav(40px) */
    z-index: 98;
  }
}

.finished-text {
  text-align: center;
  color: #969799;
  font-size: 14px;
  padding: 16px 0;
}

.list-footer {
  width: 100%;
  padding: 8px;

  .finished-text {
    text-align: center;
    color: #969799;
    font-size: 14px;
    padding: 16px 0;
  }
}

.list-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 60px; /* 底部导航栏的高度 */
}

.products-wrapper {
  flex: 1;
}
</style>
